import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Eye, EyeOff, Layers } from "lucide-react";
import DrawToolbar from "@/components/studio/DrawToolbar";
import DrawShape, { ANIM_CSS, IS_LINE } from "@/components/studio/DrawShape";

let uid = 0;
const nextId = () => `u${Date.now().toString(36)}${(uid++).toString(36)}`;

const DEFAULTS = {
  rect: { w: 200, h: 140 }, roundrect: { w: 200, h: 140 }, ellipse: { w: 160, h: 160 },
  triangle: { w: 170, h: 150 }, star: { w: 150, h: 150 }, bubble: { w: 210, h: 150 },
  arrowshape: { w: 200, h: 110 }, line: { w: 220, h: 20 }, elbow: { w: 180, h: 120 },
  text: { w: 280, h: 70 }, image: { w: 240, h: 170 }, video: { w: 260, h: 150 },
};

const newObject = (type, i) => ({
  id: nextId(), type, x: 80 + (i % 5) * 36, y: 70 + (i % 5) * 30,
  ...DEFAULTS[type], rot: 0,
  fill: type === "text" ? "none" : "#22D3EE", stroke: "#0F172A",
  strokeWidth: IS_LINE(type) ? 3 : 0, dash: "solid", opacity: 1,
  text: type === "text" ? "Your text" : "", fontSize: type === "text" ? 32 : 18,
  fontFamily: "Arial", bold: false, italic: false, color: "#0F172A", align: "center",
  arrowStart: false, arrowEnd: IS_LINE(type), group: null, imageId: null, videoId: null,
  alt: "", anim: "none", label: "",
});

const TYPE_LABEL = {
  rect: "Rectangle", roundrect: "Rounded rect", ellipse: "Ellipse", triangle: "Triangle", star: "Star",
  bubble: "Speech bubble", arrowshape: "Block arrow", line: "Line", elbow: "Elbow line", text: "Text", image: "Image", video: "Video",
};

const EDGE_SET = {
  left: (o, v) => ({ x: v }), right: (o, v) => ({ x: v - o.w }), centerX: (o, v) => ({ x: v - o.w / 2 }),
  top: (o, v) => ({ y: v }), bottom: (o, v) => ({ y: v - o.h }), centerY: (o, v) => ({ y: v - o.h / 2 }),
};

// Never hand a broken snapshot back to the page — a null doc used to blank the mission out.
const safeParse = (s) => {
  if (typeof s !== "string") return null;
  try {
    const v = JSON.parse(s);
    return v && typeof v === "object" && !Array.isArray(v) ? v : null;
  } catch (e) { return null; }
};

export default function DrawEditorCore({ doc, setDoc, config, pageRef }) {
  const objects = useMemo(() => doc.objects || [], [doc.objects]);
  const canvas = doc.canvas || { w: 960, h: 540, bg: "#FFFFFF", grid: false };
  const [sel, setSel] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [playKey, setPlayKey] = useState(0);
  const [showLayers, setShowLayers] = useState(true);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const snapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => setScale((el.clientWidth || canvas.w) / canvas.w);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canvas.w]);

  const selObjs = useMemo(() => objects.filter((o) => sel.includes(o.id)), [objects, sel]);
  const snapshot = useCallback(() => JSON.stringify(doc), [doc]);

  const commit = useCallback((next, snap) => {
    setPast((p) => [...p.slice(-40), snap ?? JSON.stringify(doc)]);
    setFuture([]);
    setDoc(next);
  }, [doc, setDoc]);

  const undo = () => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setPast((p) => p.slice(0, -1));
    const restored = safeParse(prev);
    if (!restored) return;
    setFuture((f) => [...f, JSON.stringify(doc)]);
    setDoc(restored);
    setSel([]);
  };
  const redo = () => {
    if (!future.length) return;
    const nxt = future[future.length - 1];
    setFuture((f) => f.slice(0, -1));
    const restored = safeParse(nxt);
    if (!restored) return;
    setPast((p) => [...p, JSON.stringify(doc)]);
    setDoc(restored);
    setSel([]);
  };

  const setObjects = (list, snap) => commit({ ...doc, objects: list }, snap);
  const snapVal = (v) => (canvas.grid ? Math.round(v / 10) * 10 : Math.round(v));

  const add = (type) => {
    const o = newObject(type, objects.length);
    setObjects([...objects, o]);
    setSel([o.id]);
  };

  const update = (patch) => setObjects(objects.map((o) => (sel.includes(o.id) ? { ...o, ...patch } : o)));
  const rotate = (delta) => setObjects(objects.map((o) => (sel.includes(o.id) ? { ...o, rot: (((o.rot || 0) + delta) % 360 + 360) % 360 } : o)));
  const setCanvas = (patch) => commit({ ...doc, canvas: { ...canvas, ...patch } });
  const setName = (name) => setDoc({ ...doc, name });
  const setNotes = (notes) => setDoc({ ...doc, notes });

  const arrange = (how) => {
    const list = [...objects];
    const chosen = list.filter((o) => sel.includes(o.id));
    const rest = list.filter((o) => !sel.includes(o.id));
    if (!chosen.length) return;
    if (how === "front") return setObjects([...rest, ...chosen]);
    if (how === "back") return setObjects([...chosen, ...rest]);
    const dir = how === "forward" ? 1 : -1;
    const out = [...list];
    const idxs = chosen.map((o) => out.indexOf(o));
    (dir > 0 ? idxs.reverse() : idxs).forEach((i) => {
      const j = i + dir;
      if (j < 0 || j >= out.length || sel.includes(out[j].id)) return;
      [out[i], out[j]] = [out[j], out[i]];
    });
    setObjects(out);
  };

  const group = () => {
    if (sel.length < 2) return;
    const gid = `g${nextId()}`;
    setObjects(objects.map((o) => (sel.includes(o.id) ? { ...o, group: gid } : o)));
  };
  const ungroup = () => {
    const gids = new Set(selObjs.map((o) => o.group).filter(Boolean));
    setObjects(objects.map((o) => (gids.has(o.group) ? { ...o, group: null } : o)));
  };

  const align = (edge) => {
    if (!selObjs.length) return;
    let target;
    if (selObjs.length === 1) {
      const full = { left: 0, right: canvas.w, centerX: canvas.w / 2, top: 0, bottom: canvas.h, centerY: canvas.h / 2 };
      target = full[edge];
    } else {
      const vals = selObjs.map((o) => ({ left: o.x, right: o.x + o.w, centerX: o.x + o.w / 2, top: o.y, bottom: o.y + o.h, centerY: o.y + o.h / 2 }[edge]));
      target = edge === "left" || edge === "top" ? Math.min(...vals) : edge === "right" || edge === "bottom" ? Math.max(...vals) : vals.reduce((a, b) => a + b, 0) / vals.length;
    }
    setObjects(objects.map((o) => (sel.includes(o.id) ? { ...o, ...EDGE_SET[edge](o, Math.round(target)) } : o)));
  };

  const duplicate = () => {
    const copies = selObjs.map((o) => ({ ...o, id: nextId(), x: o.x + 24, y: o.y + 24, group: null }));
    setObjects([...objects, ...copies]);
    setSel(copies.map((c) => c.id));
  };
  const remove = () => { setObjects(objects.filter((o) => !sel.includes(o.id))); setSel([]); };

  const exportPng = async () => {
    if (!pageRef?.current) return;
    const shot = await html2canvas(pageRef.current, { scale: 2, backgroundColor: canvas.bg, logging: false, useCORS: true });
    const a = document.createElement("a");
    a.href = shot.toDataURL("image/png");
    a.download = `${(doc.name || "design").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
    setDoc({ ...doc, downloadedPng: true });
  };

  const selectObject = (o, e) => {
    const ids = o.group ? objects.filter((x) => x.group === o.group).map((x) => x.id) : [o.id];
    if (e.shiftKey) setSel((s) => (s.includes(o.id) ? s.filter((x) => !ids.includes(x)) : [...s, ...ids]));
    else setSel(ids);
    return ids;
  };

  const onObjectDown = (o, e) => {
    e.stopPropagation();
    const ids = selectObject(o, e);
    snapRef.current = snapshot();
    dragRef.current = {
      mode: "move", sx: e.clientX, sy: e.clientY, s: scale,
      origs: objects.filter((x) => ids.includes(x.id)).map((x) => ({ id: x.id, x: x.x, y: x.y })),
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onHandleDown = (o, e) => {
    e.stopPropagation();
    setSel([o.id]);
    snapRef.current = snapshot();
    dragRef.current = { mode: "resize", sx: e.clientX, sy: e.clientY, s: scale, origs: [{ id: o.id, w: o.w, h: o.h }] };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / d.s, dy = (e.clientY - d.sy) / d.s;
    const map = new Map(d.origs.map((o) => [o.id, o]));
    setDoc({
      ...doc,
      objects: objects.map((o) => {
        const orig = map.get(o.id);
        if (!orig) return o;
        if (d.mode === "move") return { ...o, x: snapVal(orig.x + dx), y: snapVal(orig.y + dy) };
        return { ...o, w: Math.max(16, snapVal(orig.w + dx)), h: Math.max(16, snapVal(orig.h + dy)) };
      }),
    });
  };

  const onPointerUp = () => {
    if (dragRef.current && snapRef.current) { setPast((p) => [...p.slice(-40), snapRef.current]); setFuture([]); }
    dragRef.current = null;
    snapRef.current = null;
  };

  const act = {
    add, update, rotate, arrange, group, ungroup, align, duplicate, remove, setCanvas, setName,
    undo, redo, canUndo: past.length > 0, canRedo: future.length > 0,
    play: () => setPlayKey((k) => k + 1), exportPng,
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10">
      <DrawToolbar doc={doc} config={config} sel={sel} selObjs={selObjs} act={act} />
      <div className="bg-slate-300 p-3 sm:p-5 flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div ref={wrapRef} className="w-full overflow-hidden shadow-2xl" style={{ height: canvas.h * scale }}>
          <div
            ref={pageRef}
            data-testid="draw-canvas"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerDown={() => { setSel([]); setEditingId(null); }}
            className="relative select-none touch-none overflow-hidden"
            style={{
              width: canvas.w, height: canvas.h,
              transform: `scale(${scale})`, transformOrigin: "top left",
              background: canvas.bg,
              backgroundImage: canvas.grid ? "linear-gradient(to right, rgba(15,23,42,.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,.10) 1px, transparent 1px)" : undefined,
              backgroundSize: canvas.grid ? "10px 10px" : undefined,
            }}
          >
            {objects.map((o) => {
              const isSel = sel.includes(o.id);
              return (
                <div
                  key={o.id}
                  data-testid={`draw-obj-${o.id}`}
                  onPointerDown={(e) => onObjectDown(o, e)}
                  onDoubleClick={(e) => { e.stopPropagation(); if (!IS_LINE(o.type)) setEditingId(o.id); }}
                  className="absolute cursor-move"
                  style={{
                    left: o.x, top: o.y, width: o.w, height: o.h,
                    transform: `rotate(${o.rot || 0}deg)`, opacity: o.opacity ?? 1,
                    outline: isSel ? "2px solid #22D3EE" : "none", outlineOffset: 2,
                    ...(playKey ? ANIM_CSS[o.anim || "none"] : {}),
                  }}
                >
                  <div key={`${o.id}-${playKey}`} className="w-full h-full pointer-events-none">
                    <DrawShape o={o} gallery={config.gallery} videos={config.videos} />
                  </div>
                  {editingId === o.id && (
                    <textarea
                      data-testid={`draw-inline-text-${o.id}`} autoFocus value={o.text || ""}
                      onPointerDown={(e) => e.stopPropagation()}
                      onChange={(e) => setDoc({ ...doc, objects: objects.map((x) => (x.id === o.id ? { ...x, text: e.target.value } : x)) })}
                      onBlur={() => setEditingId(null)}
                      className="absolute inset-0 w-full h-full bg-white/85 border-2 border-[#22D3EE] rounded resize-none outline-none p-1 text-slate-900"
                      style={{ fontSize: Math.max(12, o.fontSize * 0.8), textAlign: o.align }}
                    />
                  )}
                  {isSel && (
                    <>
                      <span data-testid={`draw-size-badge-${o.id}`} className="absolute -top-6 left-0 whitespace-nowrap rounded bg-[#22D3EE] px-1.5 py-0.5 text-[11px] font-medium text-[#04121f] pointer-events-none">
                        {Math.round(o.w)} × {Math.round(o.h)}
                      </span>
                      <div data-testid={`draw-resize-${o.id}`} onPointerDown={(e) => onHandleDown(o, e)}
                        className="absolute -right-2 -bottom-2 w-4 h-4 rounded-sm bg-[#22D3EE] border border-white cursor-nwse-resize" />
                    </>
                  )}
                </div>
              );
            })}
            {objects.length === 0 && (
              <p data-testid="draw-empty-hint" className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 pointer-events-none">
                Use the shape buttons above to start your design.
              </p>
            )}
          </div>
          </div>
          <div className="mt-3 bg-white rounded-lg border border-slate-300 p-3">
            <p className="text-xs font-medium text-slate-500 mb-1">Design notes — explain your choices (balance, contrast, color, type)</p>
            <textarea data-testid="draw-notes-input" value={doc.notes || ""} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Why did you place things where you did? Which principles did you use?"
              className="w-full bg-transparent outline-none resize-none text-sm text-slate-800 placeholder:text-slate-400" />
          </div>
        </div>

        <div className="lg:w-44 shrink-0">
          <button data-testid="draw-layers-toggle" onClick={() => setShowLayers((v) => !v)}
            className="w-full h-8 rounded-md bg-slate-900 text-slate-100 text-xs inline-flex items-center justify-center gap-1.5 mb-2">
            <Layers className="w-3.5 h-3.5" /> Layers {showLayers ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          {showLayers && (
            <div data-testid="draw-layers-panel" className="rounded-lg bg-slate-900 border border-white/10 p-2 space-y-1 max-h-[420px] overflow-y-auto hq-scrollbar">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 px-1">Front → back</p>
              {[...objects].reverse().map((o) => (
                <button key={o.id} data-testid={`draw-layer-${o.id}`} onClick={() => setSel(o.group ? objects.filter((x) => x.group === o.group).map((x) => x.id) : [o.id])}
                  className={`w-full text-left px-2 py-1.5 rounded text-[11px] flex items-center gap-1.5 ${sel.includes(o.id) ? "bg-[#22D3EE]/20 text-[#22D3EE]" : "text-slate-300 hover:bg-white/5"}`}>
                  <span className="w-3 h-3 rounded-sm border border-white/30 shrink-0" style={{ background: o.fill === "none" ? "transparent" : o.fill }} />
                  <span className="truncate">{o.label || o.text || TYPE_LABEL[o.type]}</span>
                  {o.group && <span className="ml-auto text-[9px] px-1 rounded bg-white/10 shrink-0">grp</span>}
                </button>
              ))}
              {objects.length === 0 && <p className="text-[11px] text-slate-500 px-1 py-2">No shapes yet.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
